/**
 * @module common/DualAxis/hooks/useDualAxisDraft
 * @description 组合图 / 折线双 Y 轴 —— 主/次轴 draft 读写（指标联动在各自 linkage Hook）
 *
 * ## 上传结构（当前约定）
 *
 * - 主/次轴配置统一落在 `primaryYAxis` / `secondYAxis` 对象内（含 `yAxisRangeSwitch`）。
 * - 顶层仅保留 `yAxisRangeSwitch`（单轴 / 历史组合图主轴可能仍有该字段）。
 * - **不再写入** `yAxisRangeSwitch2`；读取时若 draft/base 存在该字段，仅用于兼容旧组合图展示。
 */

import { useCallback } from 'react';
import {
  readStructuredSetting,
  useChartSettingsDispatch,
  useChartSettingsSelector,
  useSettingState,
} from '../../../../context';
import { CSActionType } from '../../../../types';
import type { TAxisKey } from '../types';

export interface IUseDualAxisDraftOptions<TAxis> {
  /**
   * 从 Context 原始轴对象归一化为 UI 用的 `TAxis`。
   * @param rangeOverride - 历史顶层范围字段覆盖轴内开关（见 `buildRangeOverride`）
   */
  normalize: (raw: unknown, rangeOverride?: { yAxisRangeSwitch?: boolean }) => TAxis;
  /** 写入时合并到轴对象的额外字段（如折线双轴固定 `chartType`） */
  mergeOnPatch?: (patch: Partial<TAxis>) => Partial<TAxis>;
}

/** 顶层范围是否参与 normalize；未落库时不覆盖轴对象内字段 */
function buildRangeOverride(
  hasStored: boolean,
  value: boolean,
): { yAxisRangeSwitch?: boolean } {
  return hasStored ? { yAxisRangeSwitch: value } : {};
}

/**
 * 历史组合图次轴范围曾存顶层 `yAxisRangeSwitch2` —— 只读兼容，不参与 Schema 默认值。
 */
function readLegacySecondaryRangeOverride(
  draft: Record<string, unknown>,
  base: Record<string, unknown>,
): { yAxisRangeSwitch?: boolean } {
  if ('yAxisRangeSwitch2' in draft) {
    const value = draft.yAxisRangeSwitch2;
    return buildRangeOverride(
      typeof value === 'boolean',
      typeof value === 'boolean' ? value : false,
    );
  }
  if ('yAxisRangeSwitch2' in base) {
    const value = base.yAxisRangeSwitch2;
    return buildRangeOverride(
      typeof value === 'boolean',
      typeof value === 'boolean' ? value : false,
    );
  }
  return {};
}

export function useDualAxisDraft<TAxis extends object>(
  options: IUseDualAxisDraftOptions<TAxis>,
) {
  const { normalize, mergeOnPatch } = options;
  const dispatch = useChartSettingsDispatch();

  // ===== 1) 主轴：顶层 yAxisRangeSwitch（Schema，仅历史/单轴场景可能落库） =====
  const { value: primaryRangeSwitch } = useSettingState('yAxisRangeSwitch');
  const hasStoredPrimaryRange = useChartSettingsSelector(
    s => 'yAxisRangeSwitch' in s.draft || 'yAxisRangeSwitch' in s.base,
  );

  // ===== 2) 派生主/次轴（范围优先轴对象；次轴可回退历史 yAxisRangeSwitch2） =====
  const primaryAxis = useChartSettingsSelector(s =>
    normalize(
      readStructuredSetting(s.draft, s.base, 'primaryYAxis'),
      buildRangeOverride(hasStoredPrimaryRange, primaryRangeSwitch),
    ),
  );
  const secondaryAxis = useChartSettingsSelector(s =>
    normalize(
      readStructuredSetting(s.draft, s.base, 'secondYAxis'),
      readLegacySecondaryRangeOverride(s.draft, s.base),
    ),
  );

  /**
   * 单轴局部更新：范围开关只写入对应轴对象，与上传结构 primaryYAxis / secondYAxis 一致。
   */
  const dispatchAxis = useCallback(
    (axisKey: TAxisKey, patch: Partial<TAxis>) => {
      const current = axisKey === 'primaryYAxis' ? primaryAxis : secondaryAxis;
      const merged = mergeOnPatch
        ? { ...current, ...mergeOnPatch(patch) }
        : { ...current, ...patch };

      dispatch({
        type: CSActionType.UPDATE_DRAFT,
        payload: { [axisKey]: merged },
      });
    },
    [dispatch, mergeOnPatch, primaryAxis, secondaryAxis],
  );

  /** 指标联动等场景：主/次轴整对象替换 */
  const dispatchAxes = useCallback(
    (payload: Partial<Record<TAxisKey, TAxis>>) => {
      dispatch({
        type: CSActionType.UPDATE_DRAFT,
        payload,
      });
    },
    [dispatch],
  );

  return {
    primaryAxis,
    secondaryAxis,
    dispatchAxis,
    dispatchAxes,
  };
}
